// ============================================
// APP: NVA (React App)
// FILE: src/hooks/useUnreadMessages.ts
// PURPOSE: Track unread chat messages count
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchChatMessages, ChatMessagesResponse } from '../lib/guestApi';

// Il badge non ha fretta: un controllo al minuto basta. Con l'app in
// background non si controlla affatto: si recupera appena l'ospite torna.
const POLLING_INTERVAL = 60000;
// Al ritorno nell'app arrivano quasi sempre sia `visibilitychange` sia
// `focus`: una richiesta sola per entrambi.
const RESUME_REFETCH_GAP = 2000;

/**
 * Quanti messaggi dello staff l'ospite non ha ancora letto.
 *
 * Prima si contava "quanti messaggi stanno prima dell'ultimo letto"
 * supponendo la lista dal piu' recente, ma il server la manda dal piu'
 * vecchio: il badge contava i messaggi vecchi. Ora vale il conteggio del
 * server (`unreadCount`, basato sulla lettura vera, cioe' la PUT fatta dal
 * drawer); con un server meno recente si contano i messaggi di chat dello
 * staff ancora `isRead === false`, che non dipende dall'ordine.
 */
function countUnread(result: ChatMessagesResponse): number {
  if (typeof result.unreadCount === 'number' && result.unreadCount >= 0) {
    return result.unreadCount;
  }
  return (result.messages || []).filter(
    (msg) => msg.type === 'chat' && msg.senderType === 'admin' && msg.isRead === false
  ).length;
}

/** La chat e' aperta e sullo schermo: l'ospite sta leggendo, niente badge. */
function isChatOnScreen(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  return !document.hidden && !!(window as unknown as { __nvaGuestChatOpen?: boolean }).__nvaGuestChatOpen;
}

export const useUnreadMessages = (token: string | null, isLoggedIn: boolean) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const lastFetchRef = useRef(0);

  // Azzeramento locale (ottimistico) quando si apre la chat. La lettura vera
  // la segna il drawer sul server; l'argomento resta per compatibilita' con
  // chi chiamava markAllAsRead(id), ma non serve piu'.
  const markAllAsRead = useCallback((latestMessageId?: string) => {
    setUnreadCount(0);
  }, []);

  // Check for unread messages
  const checkUnreadMessages = useCallback(async () => {
    if (!token || !isLoggedIn) {
      setUnreadCount(0);
      return;
    }

    lastFetchRef.current = Date.now();
    try {
      const result = await fetchChatMessages(token);

      if (result.success && result.messages) {
        setUnreadCount(isChatOnScreen() ? 0 : countUnread(result));
        setLastChecked(new Date());
      }
    } catch (error) {
      console.error('Error checking unread messages:', error);
    }
  }, [token, isLoggedIn]);

  // Polling ogni 60s, fermo quando l'app non si vede (telefono in tasca,
  // altra scheda): richieste inutili e batteria. Al ritorno si ricontrolla
  // subito e il minuto riparte da li'.
  useEffect(() => {
    if (!isLoggedIn || !token) {
      setUnreadCount(0);
      return;
    }

    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const start = () => {
      stop();
      timer = setInterval(() => {
        if (!document.hidden) checkUnreadMessages();
      }, POLLING_INTERVAL);
    };
    const resume = () => {
      if (document.hidden) return;
      if (Date.now() - lastFetchRef.current >= RESUME_REFETCH_GAP) checkUnreadMessages();
      start();
    };
    const onVisibilityChange = () => {
      if (document.hidden) stop();
      else resume();
    };

    checkUnreadMessages();
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', resume);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', resume);
    };
  }, [isLoggedIn, token, checkUnreadMessages]);

  // Eventi della chat:
  //  - openGuestChat: la chat si apre, il badge sparisce subito (il drawer
  //    segnera' la lettura sul server appena carica i messaggi);
  //  - nva_chat_read: il drawer ha segnato tutto come letto sul server;
  //  - nva_chat_reaction: push in primo piano (messaggio o reazione dello
  //    staff): il badge si aggiorna senza aspettare il minuto.
  useEffect(() => {
    if (!token || !isLoggedIn) return;

    const handleChatOpened = () => { markAllAsRead(); };
    const handleChatRead = () => { setUnreadCount(0); };
    const handleChatPush = () => { checkUnreadMessages(); };

    window.addEventListener('openGuestChat', handleChatOpened);
    window.addEventListener('nva_chat_read', handleChatRead);
    window.addEventListener('nva_chat_reaction', handleChatPush);

    return () => {
      window.removeEventListener('openGuestChat', handleChatOpened);
      window.removeEventListener('nva_chat_read', handleChatRead);
      window.removeEventListener('nva_chat_reaction', handleChatPush);
    };
  }, [token, isLoggedIn, markAllAsRead, checkUnreadMessages]);

  return {
    unreadCount,
    lastChecked,
    checkUnreadMessages,
    markAllAsRead
  };
};
